import { useState, useEffect } from 'react';
import toast from 'react-hot-toast';
import { inputStyles, labelStyles, requiredStyles } from './styles';
import { apiUrl, authHeaders } from '~/services/auth';
import { formatPhoneNumber } from '~/common/form/FormInput';
import Select from 'react-select';

// Matches the (XXX) XXX-XXXX shape the rest of the admin forms validate against.
const PHONE_PATTERN = /^\(\d{3}\) \d{3}-\d{4}$/;

interface Props {
    location: string;
    selectedMedicalCenter?: { value: number; label: string } | null;
    onCancel: () => void;
    onSuccess: (response: any) => void;
    profOnly?: boolean;
    applicationId?: number;
    campaignRequestId?: number;
}

interface MedicalProfessionalType {
    id: number;
    name: string;
}

const IDENTITY_CHOICES = [
    { value: 1, label: 'Client' },
    { value: 2, label: 'Spouse' },
    { value: 3, label: 'Parent of minor client' },
    { value: 4, label: 'Legal Guardian' },
    { value: 5, label: 'POA' },
];

const PHONE_TYPE_CHOICES = [
    { value: 'Cell', label: 'Cell' },
    { value: 'Home', label: 'Home' },
    { value: 'Office', label: 'Office' },
    { value: 'Fax', label: 'Fax' },
    { value: 'Pager', label: 'Pager' },
    { value: 'Main', label: 'Main' },
    { value: 'Direct', label: 'Direct' },
];

// API function to add new medical professional (uses new_admin endpoints)
const addNewMedicalProfessional = async (
    medCenterName: string | null,
    medCenterId: number | null,
    location: string,
    person: {
        first_name: string;
        middle_name?: string;
        last_name: string;
        email?: string;
        phone?: string;
        phone_type?: string;
    },
    medicalProfessionalType: number | null,
    mpGiveConsentForMpToProvideInfo: boolean,
    mpCertifyIdentity: number,
    mpSignature: string,
    applicationId?: number,
    campaignRequestId?: number,
) => {
    const headers = authHeaders();

    // Use the appropriate admin endpoint based on context. The Campaign Detail
    // page has a campaign request; the Campaign Application Detail page only
    // has an application id, and campaigns predating the online application
    // have no campaign request, so the application endpoint is the fallback.
    let url: string;
    if (campaignRequestId) {
        url = `${apiUrl}/admin/campaignRequests/${campaignRequestId}/addMedicalProfessional`;
    } else if (applicationId) {
        url = `${apiUrl}/admin/campaignApplications/${applicationId}/addMedicalProfessional`;
    } else {
        throw new Error(
            'Either applicationId or campaignRequestId is required',
        );
    }

    const resp = await fetch(url, {
        method: 'POST',
        headers,
        body: JSON.stringify({
            med_center_name: medCenterName,
            med_center_id: medCenterId,
            location,
            person,
            medical_professional_type: medicalProfessionalType,
            mp_give_consent_for_mp_to_provide_info:
                mpGiveConsentForMpToProvideInfo,
            mp_certify_identity: mpCertifyIdentity,
            mp_signature: mpSignature,
        }),
    });

    if (!resp.ok) {
        // The backend explains the ones CS can act on — a duplicate email, for
        // instance (HHLTB-2722). Show that message on its own rather than
        // wrapping the raw payload in a prefix.
        const errorText = await resp.text();
        let detail = '';
        try {
            const parsed = JSON.parse(errorText);
            detail = parsed?.error || parsed?.detail || '';
        } catch {
            detail = '';
        }
        throw new Error(
            detail || `Failed to add medical professional: ${errorText}`,
        );
    }
    return await resp.json();
};

// Fetch medical professional types
const fetchMedicalProfessionalTypes = async () => {
    const headers = authHeaders();
    const url = `${apiUrl}/v2/options/medicalProfessionalTypes`;
    const resp = await fetch(url, {
        method: 'GET',
        headers,
    });
    if (!resp.ok) throw new Error('Failed to fetch medical professional types');
    return await resp.json();
};

const MedCenterForm: React.FC<Props> = ({
    location,
    selectedMedicalCenter = null,
    onCancel,
    onSuccess,
    profOnly = false,
    applicationId,
    campaignRequestId,
}) => {
    const [medicalProfessionalTypes, setMedicalProfessionalTypes] = useState<
        MedicalProfessionalType[]
    >([]);
    const [isSubmitting, setIsSubmitting] = useState(false);

    // Form state
    const [medCenterName, setMedCenterName] = useState('');
    const [medProfFirstName, setMedProfFirstName] = useState('');
    const [medProfMiddleName, setMedProfMiddleName] = useState('');
    const [medProfLastName, setMedProfLastName] = useState('');
    const [medProfType, setMedProfType] = useState<number | null>(null);
    const [medProfEmail, setMedProfEmail] = useState('');
    const [medProfPhone, setMedProfPhone] = useState('');
    const [phoneError, setPhoneError] = useState('');
    const [medProfPhoneType, setMedProfPhoneType] = useState<string>('Direct');
    const [consent, setConsent] = useState(false);
    const [certifyIdentity, setCertifyIdentity] = useState<number | null>(null);
    const [signature, setSignature] = useState('');

    useEffect(() => {
        const loadMedicalProfessionalTypes = async () => {
            try {
                const types = await fetchMedicalProfessionalTypes();
                setMedicalProfessionalTypes(types);
            } catch (error) {
                console.error(
                    'Error loading medical professional types:',
                    error,
                );
            }
        };
        loadMedicalProfessionalTypes();
    }, []);

    const medProfTypeOptions = medicalProfessionalTypes.map((type) => ({
        value: type.id,
        label: type.name,
    }));

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();

        if (
            !certifyIdentity ||
            !signature.trim() ||
            !medProfFirstName.trim() ||
            !medProfLastName.trim() ||
            !medProfType
        ) {
            toast.error('Please fill in all required fields');
            return;
        }

        if (!profOnly && !medCenterName.trim()) {
            toast.error('Please enter a medical center name');
            return;
        }

        // Phone is optional, but an entered number has to be complete so the
        // saved value matches the format the med prof drawer validates against.
        if (medProfPhone && !PHONE_PATTERN.test(medProfPhone)) {
            setPhoneError(
                'Please enter a valid phone number in format (123) 456-7890',
            );
            return;
        }
        setPhoneError('');

        setIsSubmitting(true);
        try {
            const person = {
                first_name: medProfFirstName,
                middle_name: medProfMiddleName,
                last_name: medProfLastName,
                email: medProfEmail,
                phone: medProfPhone,
                phone_type: medProfPhoneType,
            };

            const response = await addNewMedicalProfessional(
                profOnly ? null : medCenterName,
                selectedMedicalCenter ? selectedMedicalCenter.value : null,
                location,
                person,
                medProfType,
                consent,
                certifyIdentity,
                signature,
                applicationId,
                campaignRequestId,
            );

            toast.success(
                profOnly
                    ? 'Medical professional added'
                    : 'Medical center and professional added',
            );
            onSuccess(response);
        } catch (error) {
            console.error('Error adding medical professional:', error);
            // Some failures are the user's to resolve — a medical professional
            // who already exists, say — so show what the backend said instead
            // of a blanket "try again" that repeats the same failure
            // (HHLTB-2722).
            const message =
                error instanceof Error && error.message
                    ? error.message
                    : 'Failed to add medical professional. Please try again.';
            toast.error(message);
        } finally {
            setIsSubmitting(false);
        }
    };

    const isValid =
        (profOnly || medCenterName.trim()) &&
        medProfFirstName.trim() &&
        medProfLastName.trim() &&
        medProfType &&
        consent &&
        certifyIdentity &&
        signature.trim() &&
        !isSubmitting;

    return (
        <div className='mb-[16px] border-l-4 border-[#FEBF3E] bg-[#FEF9EC] p-[16px]'>
            <h3 className='mb-[16px] text-[16px] font-bold'>
                {profOnly
                    ? 'Add New Medical Professional'
                    : 'Add New Medical Center & Professional'}
            </h3>

            <div>
                {!profOnly && (
                    <>
                        <label
                            htmlFor='med-center-name'
                            className={labelStyles}
                        >
                            Medical Center Name
                            <span className={requiredStyles}>*</span>
                        </label>
                        <input
                            type='text'
                            id='med-center-name'
                            className={`${inputStyles} mb-[16px] w-full`}
                            value={medCenterName}
                            onChange={(e) => setMedCenterName(e.target.value)}
                            disabled={isSubmitting}
                        />
                    </>
                )}

                <label htmlFor='med-prof-first-name' className={labelStyles}>
                    Medical Professional First Name
                    <span className={requiredStyles}>*</span>
                </label>
                <input
                    type='text'
                    id='med-prof-first-name'
                    className={`${inputStyles} mb-[16px] w-full`}
                    value={medProfFirstName}
                    onChange={(e) => setMedProfFirstName(e.target.value)}
                    disabled={isSubmitting}
                />

                <label htmlFor='med-prof-middle-name' className={labelStyles}>
                    Medical Professional Middle Name
                </label>
                <input
                    type='text'
                    id='med-prof-middle-name'
                    className={`${inputStyles} mb-[16px] w-full`}
                    value={medProfMiddleName}
                    onChange={(e) => setMedProfMiddleName(e.target.value)}
                    disabled={isSubmitting}
                />

                <label htmlFor='med-prof-last-name' className={labelStyles}>
                    Medical Professional Last Name
                    <span className={requiredStyles}>*</span>
                </label>
                <input
                    type='text'
                    id='med-prof-last-name'
                    className={`${inputStyles} mb-[16px] w-full`}
                    value={medProfLastName}
                    onChange={(e) => setMedProfLastName(e.target.value)}
                    disabled={isSubmitting}
                />

                <label htmlFor='med-prof-type' className={labelStyles}>
                    Medical Professional Type
                    <span className={requiredStyles}>*</span>
                </label>
                <Select
                    menuPlacement='auto'
                    id='med-prof-type'
                    options={medProfTypeOptions}
                    value={
                        medProfType
                            ? medProfTypeOptions.find(
                                  (opt) => opt.value === medProfType,
                              )
                            : null
                    }
                    onChange={(option) =>
                        setMedProfType(option ? option.value : null)
                    }
                    placeholder='Select a type...'
                    className='mb-[16px]'
                    isDisabled={isSubmitting}
                />

                <label htmlFor='med-prof-email' className={labelStyles}>
                    Medical Professional Email
                </label>
                <input
                    type='email'
                    id='med-prof-email'
                    className={`${inputStyles} mb-[16px] w-full`}
                    value={medProfEmail}
                    onChange={(e) => setMedProfEmail(e.target.value)}
                    disabled={isSubmitting}
                />

                <div className='mb-[16px] grid grid-cols-2 gap-[12px]'>
                    <div>
                        <label htmlFor='med-prof-phone' className={labelStyles}>
                            Medical Professional Phone
                        </label>
                        <input
                            type='tel'
                            id='med-prof-phone'
                            className={`${inputStyles} w-full`}
                            placeholder='(123) 456-7890'
                            maxLength={14}
                            value={medProfPhone}
                            onChange={(e) => {
                                setMedProfPhone(
                                    formatPhoneNumber(e.target.value),
                                );
                                setPhoneError('');
                            }}
                            disabled={isSubmitting}
                        />
                        {phoneError && (
                            <p className='mt-[4px] text-[12px] text-red'>
                                {phoneError}
                            </p>
                        )}
                    </div>
                    <div>
                        <label
                            htmlFor='med-prof-phone-type'
                            className={labelStyles}
                        >
                            Phone Type
                        </label>
                        <Select
                            menuPlacement='auto'
                            id='med-prof-phone-type'
                            options={PHONE_TYPE_CHOICES}
                            value={PHONE_TYPE_CHOICES.find(
                                (opt) => opt.value === medProfPhoneType,
                            )}
                            onChange={(option) =>
                                setMedProfPhoneType(
                                    option ? option.value : 'Direct',
                                )
                            }
                            placeholder='Select phone type...'
                            isDisabled={isSubmitting}
                        />
                    </div>
                </div>

                <div className='mb-[16px]'>
                    <label className='flex items-start gap-[8px]'>
                        <input
                            type='checkbox'
                            checked={consent}
                            onChange={(e) => setConsent(e.target.checked)}
                            disabled={isSubmitting}
                            className='mt-[4px]'
                        />
                        <span className='text-[14px]/[20px]'>
                            I give consent to their health care professional to
                            provide information to Help Hope Live confirming
                            their medical and financial need for the purpose of
                            fundraising.
                            <span className={requiredStyles}>*</span>
                        </span>
                    </label>
                </div>

                <label htmlFor='med-certify-identity' className={labelStyles}>
                    I certify that I am the:
                    <span className={requiredStyles}>*</span>
                </label>
                <Select
                    menuPlacement='auto'
                    id='med-certify-identity'
                    options={IDENTITY_CHOICES}
                    value={IDENTITY_CHOICES.find(
                        (opt) => opt.value === certifyIdentity,
                    )}
                    onChange={(option) =>
                        setCertifyIdentity(option ? option.value : null)
                    }
                    placeholder='Select...'
                    className='mb-[16px]'
                    isDisabled={isSubmitting}
                />

                <label htmlFor='med-signature' className={labelStyles}>
                    Name of individual saving these agreements
                    <span className={requiredStyles}>*</span>
                </label>
                <input
                    type='text'
                    id='med-signature'
                    className={`${inputStyles} mb-[16px] w-full`}
                    value={signature}
                    onChange={(e) => setSignature(e.target.value)}
                    disabled={isSubmitting}
                />

                <div className='flex gap-[12px]'>
                    <button
                        type='button'
                        className='grow rounded-full border-[2px] px-[20px] py-[6px] text-[14px]/[18px] font-bold uppercase transition-colors duration-500 hover:border-black hover:bg-black hover:text-white'
                        onClick={onCancel}
                        disabled={isSubmitting}
                    >
                        Cancel
                    </button>
                    <button
                        type='button'
                        className='grow rounded-full border-[2px] border-[#FEBF3E] bg-[#FEBF3E] px-[20px] py-[6px] text-[14px]/[18px] font-bold uppercase transition-colors duration-500 hover:border-[#E6F7F8] hover:bg-[#E6F7F8] disabled:cursor-not-allowed disabled:opacity-50'
                        disabled={!isValid}
                        onClick={(e) => {
                            e.preventDefault();
                            handleSubmit(e);
                        }}
                    >
                        {isSubmitting ? (
                            <span className='flex items-center justify-center'>
                                <svg
                                    className='mr-2 h-4 w-4 animate-spin'
                                    viewBox='0 0 24 24'
                                >
                                    <circle
                                        className='opacity-25'
                                        cx='12'
                                        cy='12'
                                        r='10'
                                        stroke='currentColor'
                                        strokeWidth='4'
                                    />
                                    <path
                                        className='opacity-75'
                                        fill='currentColor'
                                        d='M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z'
                                    />
                                </svg>
                                Saving...
                            </span>
                        ) : (
                            'Submit'
                        )}
                    </button>
                </div>
            </div>
        </div>
    );
};

export default MedCenterForm;
